import { selectTagsStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Checkbox, Col, Form, Input, Modal, Row, Select, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HttpContentType } from "../../../../../common/api/http";
import { useImportContracts } from "../../../../../common/api/queries";
import ActionButton from "../../../../../common/components/buttons/ActionButton";
import LabelWithTooltip from "../../../../../common/components/form/labels/LabelWithTooltip";
import { FileUploadAccept, ModalSizes, rowGutter } from "../../../../../common/constants";
import { ContractVerificationStatus, contractVerificationStatusTMap } from "../../../enums";
import ContractVerificationStatusTag from "../../tags/ContractVerificationStatusTag";

interface Props {
  open: boolean;
  onFormCancel: () => void;
}

const ContractsImportForm = ({ open, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<{
    dryRun: boolean;
    allowUpdate: boolean;
    verificationStatus: ContractVerificationStatus;
    verificationNote: string;
  }>();

  const importContracts = useImportContracts();

  const [file, setFile] = useState<RcFile>();

  const handleFileUpload = (file: RcFile): boolean | string => {
    if (!HttpContentType.CSV.includes(file.type)) {
      notification.error({ title: t("common.error"), description: t("helpers.onlyCsvSupported") });
      return Upload.LIST_IGNORE;
    }
    setFile(file);
    return false;
  };

  const handleFileRemove = () => {
    setFile(undefined);
    return true;
  };

  const handleAfterClosed = () => {
    setFile(undefined);
    form.resetFields();
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!file) {
          notification.error({ title: t("common.error"), description: t("helpers.missingInputFile") });
        } else {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("dryRun", values.dryRun.toString());
          formData.append("allowUpdate", values.allowUpdate.toString());
          formData.append("verificationStatus", values.verificationStatus);

          if (values.verificationNote) {
            formData.append("verificationNote", values.verificationNote);
          }

          importContracts.mutate(formData, {
            onError: (error) => setErrorsToForm(form, "contract.helpers.import", error.violations),
            onSuccess: onFormCancel,
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("contract.titles.import")}
      okText={t("common.submit")}
      cancelText={t("common.cancel")}
      confirmLoading={importContracts.isPending}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      afterClose={handleAfterClosed}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="contractsImportForm">
        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="dryRun" valuePropName="checked" rules={[validations.none]} initialValue={true}>
              <Checkbox>
                <LabelWithTooltip
                  label={t("contract.helpers.import.dryRun")}
                  tooltip={t("contract.helpers.import.dryRunDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="allowUpdate" valuePropName="checked" rules={[validations.none]} initialValue={false}>
              <Checkbox>
                <LabelWithTooltip
                  label={t("contract.helpers.import.allowUpdate")}
                  tooltip={t("contract.helpers.import.allowUpdateDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="verificationStatus"
              label={
                <LabelWithTooltip
                  label={t("contract.enums.verificationStatus._label")}
                  tooltip={t("contract.helpers.import.verificationStatusDesc")}
                />
              }
              rules={[validations.notNull]}
            >
              <Select
                {...selectTagsStandardProps(contractVerificationStatusTMap)}
                options={Object.values(ContractVerificationStatus).map((status) => ({
                  value: status,
                  label: <ContractVerificationStatusTag status={status} />,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item noStyle shouldUpdate={(prev, next) => prev.verificationStatus !== next.verificationStatus}>
          {({ getFieldValue }) => {
            const status = getFieldValue("verificationStatus") as ContractVerificationStatus;
            return status === ContractVerificationStatus.SENT ||
              status === ContractVerificationStatus.INTERNAL_INTERVENTION ||
              status === ContractVerificationStatus.EXTERNAL_INTERVENTION ||
              status === ContractVerificationStatus.INTERNAL_AND_EXTERNAL_INTERVENTION ? (
              <Row gutter={rowGutter}>
                <Col span={24}>
                  <Form.Item
                    name="verificationNote"
                    label={t("contract.helpers.import.verificationNote")}
                    rules={[validations.size(1, 1024)]}
                  >
                    <Input.TextArea autoSize={{ minRows: 3, maxRows: 15 }} />
                  </Form.Item>
                </Col>
              </Row>
            ) : null;
          }}
        </Form.Item>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Upload
              maxCount={1}
              accept={FileUploadAccept.CSV}
              fileList={file ? [file] : []}
              beforeUpload={handleFileUpload}
              onRemove={handleFileRemove}
            >
              <ActionButton icon="plus">{t("common.chooseFile")}</ActionButton>
            </Upload>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ContractsImportForm;
